import { useState, useRef, useEffect, FC, useMemo } from 'react';
import { Box, Typography, IconButton, Tooltip, Stack, CircularProgress } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendIcon from '@mui/icons-material/Send';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import useNoteFormatDisplayStore from '@/stores/noteFormatDisplayStore';
import { errorReporter } from '@/services/errorReportingService';
import useAlertStore from '@/stores/alertStore';
import { useAuthStore } from '@/stores/authStore';
import { hasBetaFeature } from '@/utils/subscriptionHelpers';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import { useSendFormatToZapier } from '@/features/zapier/hooks/useSendFormatToZapier';
import type { UserWithZapier } from '@/features/zapier/types';

/**
 * Converts a JSON value to human-readable markdown recursively.
 */
function jsonValueToMarkdown(value: unknown, depth: number): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const entries = Object.entries(item as Record<string, unknown>);
          const firstVal = entries.length > 0 ? String(entries[0][1] ?? '') : '';
          const rest = entries.slice(1);
          const subFields = rest
            .map(([k, v]) => `  - **${formatKey(k)}:** ${jsonValueToMarkdown(v, depth + 1)}`)
            .join('\n');
          return `- **${firstVal}**${subFields ? '\n' + subFields : ''}`;
        }
        return `- ${jsonValueToMarkdown(item, depth + 1)}`;
      })
      .join('\n');
  }

  if (typeof value === 'object') {
    return objectToMarkdown(value as Record<string, unknown>, depth);
  }

  return String(value);
}

function formatKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function objectToMarkdown(obj: Record<string, unknown>, depth: number): string {
  const headingPrefix = '#'.repeat(Math.min(depth + 1, 4));
  const lines: string[] = [];

  for (const [key, value] of Object.entries(obj)) {
    const label = formatKey(key);

    if (Array.isArray(value)) {
      lines.push(`${headingPrefix} ${label}\n`);
      lines.push(jsonValueToMarkdown(value, depth + 1));
      lines.push('');
    } else if (value && typeof value === 'object') {
      lines.push(`${headingPrefix} ${label}\n`);
      lines.push(objectToMarkdown(value as Record<string, unknown>, depth + 1));
      lines.push('');
    } else {
      lines.push(`**${label}:** ${jsonValueToMarkdown(value, depth)}\n`);
    }
  }

  return lines.join('\n');
}

function tryJsonToMarkdown(content: string): string | null {
  let trimmed = content.trim();

  // Strip markdown code fences (```json ... ``` or ``` ... ```)
  const codeFenceMatch = trimmed.match(/^```(?:\w+)?\s*\n?([\s\S]*?)\n?```$/);
  if (codeFenceMatch) {
    trimmed = codeFenceMatch[1].trim();
  }

  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (typeof parsed === 'object' && parsed !== null) {
      if (Array.isArray(parsed)) {
        return jsonValueToMarkdown(parsed, 0);
      }
      return objectToMarkdown(parsed as Record<string, unknown>, 0);
    }
    return null;
  } catch {
    return null;
  }
}

interface InlineFormatResultProps {
  hideCloseButton?: boolean;
  readOnly?: boolean;
}

const InlineFormatResult: FC<InlineFormatResultProps> = ({ hideCloseButton = false, readOnly = false }) => {
  const activeResult = useNoteFormatDisplayStore((s) => s.activeResult);
  const activeDisplayName = useNoteFormatDisplayStore((s) => s.activeDisplayName);
  const isRegenerating = useNoteFormatDisplayStore((s) => s.isRegenerating);
  const regenerateHandler = useNoteFormatDisplayStore((s) => s.regenerateHandler);
  const deleteHandler = useNoteFormatDisplayStore((s) => s.deleteHandler);
  const clearActiveFormat = useNoteFormatDisplayStore((s) => s.clearActiveFormat);
  const { showAlert } = useAlertStore();
  const { userData } = useAuthStore();
  const { betaFeatures } = useCurrentPlanFeatures();
  const { sendFormatToZapier, isSending: isSendingToZapier } = useSendFormatToZapier();

  const zapierUser = userData as UserWithZapier | null;
  const hasFormatWebhook =
    !readOnly &&
    hasBetaFeature(userData, betaFeatures, 'zapier') &&
    !!activeResult?.prompt_value &&
    !!zapierUser?.zapier_format_webhooks?.[activeResult.prompt_value];

  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setCopied(false);
  }, [activeResult?.id]);

  const handleCopy = async () => {
    if (!activeResult?.prompt_result) return;
    try {
      await navigator.clipboard.writeText(activeResult.prompt_result);
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      errorReporter.expected('Failed to copy format result to clipboard', error, {
        feature: 'noteFormats',
        operation: 'copyToClipboard',
      });
      showAlert('Failed to copy to clipboard. Please try selecting and copying the text manually.', 'warning');
    }
  };

  const displayContent = useMemo(() => {
    if (!activeResult?.prompt_result) return null;
    return tryJsonToMarkdown(activeResult.prompt_result) ?? activeResult.prompt_result;
  }, [activeResult?.prompt_result]);

  if (!activeResult) return null;

  return (
    <Box sx={{ mt: 4, maxWidth: '800px', width: '100%' }}>
      {/* Title row — matches Summary section pattern */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: '1rem', mb: 0 }}>
        <Typography
          variant="button"
          component="div"
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            color: 'text.primary',
            fontWeight: 600,
          }}
        >
          {activeDisplayName}
        </Typography>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title={copied ? 'Copied!' : 'Copy'}>
            <IconButton size="small" onClick={handleCopy}>
              {copied ? (
                <CheckIcon sx={{ fontSize: 18 }} color="success" />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              )}
            </IconButton>
          </Tooltip>

          {hasFormatWebhook && (
            <Tooltip title="Send to Zapier">
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (activeResult?.note_id && activeResult?.id) {
                      void sendFormatToZapier(activeResult.note_id, activeResult.id);
                    }
                  }}
                  disabled={isSendingToZapier}
                >
                  {isSendingToZapier ? (
                    <CircularProgress size={18} />
                  ) : (
                    <SendIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {!readOnly && (
            <Tooltip title="Regenerate">
              <span>
                <IconButton size="small" onClick={() => regenerateHandler?.()} disabled={isRegenerating}>
                  {isRegenerating ? (
                    <CircularProgress size={18} />
                  ) : (
                    <RefreshIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {!readOnly && (
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => deleteHandler?.()}>
                <DeleteOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} />
              </IconButton>
            </Tooltip>
          )}

          {!hideCloseButton && (
            <Tooltip title="Close">
              <IconButton size="small" onClick={clearActiveFormat}>
                <CloseIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {/* Content — same styling as Summary text field */}
      <Box
        sx={{
          bgcolor: 'common.white',
          borderRadius: '13px',
          px: 1.75,
          py: 1.5,
          fontSize: '14px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          lineHeight: 1.43,
          letterSpacing: '0.4px',
          color: 'text.primary',
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: 1.43,
            letterSpacing: '0.4px',
            mt: 2,
            mb: 0.5,
          },
          '& p': {
            fontSize: '14px',
            lineHeight: 1.43,
            letterSpacing: '0.4px',
            mt: 0,
            mb: 1,
          },
          '& ul, & ol': {
            pl: 2.5,
            my: 0.5,
          },
          '& li': {
            fontSize: '14px',
            lineHeight: 1.43,
            letterSpacing: '0.4px',
            mb: 0.25,
          },
          '& code': {
            bgcolor: 'grey.100',
            borderRadius: 0.5,
            px: 0.5,
            py: 0.25,
            fontSize: '0.85em',
            fontFamily: 'monospace',
          },
          '& pre': {
            bgcolor: 'grey.100',
            borderRadius: 1,
            p: 1.5,
            overflow: 'auto',
            fontSize: '0.85em',
            fontFamily: 'monospace',
            my: 1,
          },
          '& blockquote': {
            borderLeft: 3,
            borderColor: 'divider',
            pl: 2,
            my: 1,
            color: 'text.secondary',
          },
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            my: 1,
          },
          '& th, & td': {
            border: 1,
            borderColor: 'divider',
            px: 1,
            py: 0.5,
            fontSize: '13px',
          },
          '& th': {
            bgcolor: 'grey.50',
            fontWeight: 600,
          },
          '& hr': {
            borderColor: 'divider',
            my: 1.5,
          },
          '& img': { maxWidth: '100%', borderRadius: 1 },
          '& > *:last-child': {
            mb: 0,
          },
        }}
      >
        {displayContent ? (
          <Markdown remarkPlugins={[remarkGfm]}>{displayContent}</Markdown>
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            No content available
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default InlineFormatResult;
